import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Chip,
  CircularProgress, Alert, Divider, Stack, TextField, Autocomplete, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, Collapse,
  List, ListItemButton, ListItemIcon, ListItemText,
  Select, MenuItem, FormControl, InputLabel,
  Breadcrumbs, Link, Avatar, Menu,
  Checkbox, FormControlLabel, LinearProgress,
  ToggleButtonGroup, ToggleButton,
  useMediaQuery, useTheme, Drawer, AppBar, Toolbar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SchoolIcon from '@mui/icons-material/School';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EventNoteIcon from '@mui/icons-material/EventNote';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import LogoutIcon from '@mui/icons-material/Logout';
import PaletteIcon from '@mui/icons-material/Palette';
import LightModeIcon from '@mui/icons-material/LightMode';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import MenuIcon from '@mui/icons-material/Menu';
import { applyCssVars } from '../theme';
import { parseLocalDate } from '../shared/eventDate';
import { adminClient } from './data/adminClient';
import EventRegistrationSection, { REG_SUBSECTIONS } from './EventRegistrationSection';
import AwardsSection, { AWARDS_SUBSECTIONS } from './awards/AwardsSection';
import WorkshopsSection, { WORKSHOP_SUBSECTIONS } from './workshops/WorkshopsSection';
import PrintDialog from './PrintDialog';
import TestSchoolChip from './TestSchoolChip';
import PrintIcon from '@mui/icons-material/Print';
import EventTimelineSection from './EventTimelineSection';
import EventPricesSection from './EventPricesSection';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { EventReadOnlyContext, useEventReadOnly } from './adminAccess';
import UserManagementSection from './UserManagementSection';
import logoLight from '../OSSDF LOGO.png';
import logoDark from '../OSSDF LOGO INVERTED.png';
import usePageTitle from '../shared/hooks/usePageTitle';

// Sidebar used to be permanently dark regardless of the app's light/dark
// theme — these are its two color sets, keyed off the same resolved mode
// that drives the main panel via the MUI theme. Light values are tied to
// theme.js's colorTokens.light (surface/text/secondary) so the sidebar stays
// visually consistent with the rest of the app in light mode.
const SIDEBAR_PALETTE = {
  dark: {
    bg: '#1A1A2E',
    border: 'rgba(255,255,255,0.08)',
    text: '#fff',
    textMuted: 'rgba(255,255,255,0.6)',
    textFaint: 'rgba(255,255,255,0.5)',
    hover: 'rgba(255,255,255,0.08)',
    accent: '#90CAF9',
    selectedBg: 'rgba(144,202,249,0.15)',
    selectedBgHover: 'rgba(144,202,249,0.2)',
    inputBg: 'rgba(255,255,255,0.06)',
    inputBorder: 'rgba(255,255,255,0.15)',
    inputBorderHover: 'rgba(255,255,255,0.3)',
  },
  light: {
    bg: '#fcfcf3',
    border: 'rgba(0,0,0,0.1)',
    text: '#101010',
    textMuted: 'rgba(0,0,0,0.6)',
    textFaint: 'rgba(0,0,0,0.45)',
    hover: 'rgba(0,0,0,0.05)',
    accent: '#0074D9',
    selectedBg: 'rgba(0,116,217,0.1)',
    selectedBgHover: 'rgba(0,116,217,0.16)',
    inputBg: 'rgba(0,0,0,0.03)',
    inputBorder: 'rgba(0,0,0,0.18)',
    inputBorderHover: 'rgba(0,0,0,0.32)',
  },
};

// Restores which page (and, for Event Management, which sub/sub-section) the
// admin was last viewing across a browser refresh. Stored as one JSON blob
// so the three stay consistent with each other. Safe against stale/invalid
// values: activePage just renders nothing until the admin clicks a nav item
// (same as any other unrecognized value would today), and the privilege-
// based redirect effect below only overrides when activePage is still the
// hardcoded 'home' default, so it never clobbers a restored non-home page.
const ADMIN_NAV_STORAGE_KEY = 'admin:navState';
function loadStoredAdminNav() {
  try {
    return JSON.parse(localStorage.getItem(ADMIN_NAV_STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}

// Pinned "Quick Links" — page ids (see pageId()) the admin has starred for
// one-click access from the sidebar, regardless of what page they're
// currently on. Persisted the same way as admin:navState.
const PINNED_PAGES_STORAGE_KEY = 'admin:pinnedPages';
function loadStoredPinnedPages() {
  try {
    const stored = JSON.parse(localStorage.getItem(PINNED_PAGES_STORAGE_KEY));
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
}

export default function AdminDashboard({ setMode }) {
  usePageTitle('OSSDF Admin');
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [approving, setApproving] = useState(null);
  const [approvedIds, setApprovedIds] = useState({});
  const [credentials, setCredentials] = useState(null);
  const [copied, setCopied] = useState(null);
  const [approvedSchools, setApprovedSchools] = useState([]);
  const [approvedLoading, setApprovedLoading] = useState(true);
  const [resetting, setResetting] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [activePage, setActivePage] = useState(() => loadStoredAdminNav().activePage || 'home');
  const [eventSection, setEventSection] = useState(() => loadStoredAdminNav().eventSection ?? null);
  const [regSection, setRegSection] = useState(() => loadStoredAdminNav().regSection ?? null);
  const [pinnedPages, setPinnedPages] = useState(loadStoredPinnedPages);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [accountAnchor, setAccountAnchor] = useState(null);
  const [menuView, setMenuView] = useState('main');
  const [searchInput, setSearchInput] = useState('');
  // Below this width the permanent sidebar becomes a collapsible Drawer —
  // same breakpoint TeacherApp.js uses for its own mobile layout.
  const isMobile = useMediaQuery('(max-width:768px)');
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleExpanded = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const portals = user?.portals || [];
  const hasAccountAdmin = portals.includes('accountAdmin');
  const hasEventAdmin = portals.includes('eventAdmin');
  // hasBoth gates money pages (School Payments, editing School Registrations).
  const hasBoth = hasAccountAdmin && hasEventAdmin;
  // Event Viewers see Event Management (minus the budget) but can't change it.
  const hasEventView = hasEventAdmin || portals.includes('eventViewer');
  const eventReadOnly = hasEventView && !hasEventAdmin;
  // Workshops: Event Admins edit and Event Viewers see them inside Event
  // Management, as before. The Users tab's Workshops column only ever adds
  // to that — an Event Viewer given Workshops Edit can edit workshops.
  const workshopReadOnly = !hasEventAdmin && !portals.includes('workshopAdmin');
  // Workshops-only users (no Event Management access) get Workshops as its
  // own area instead, with nothing else of Event Management.
  const hasWorkshopArea = !hasEventView && (portals.includes('workshopAdmin') || portals.includes('workshopViewer'));
  // More than one area visible — they get a Home page.
  const hasBothAreas = [hasAccountAdmin, hasEventView, hasWorkshopArea].filter(Boolean).length > 1;
  // Where a single-area user lands (and what the logo returns to).
  const defaultPage = hasBothAreas ? 'home' : hasAccountAdmin ? 'schoolAccounts' : hasEventView ? 'events' : hasWorkshopArea ? 'workshops' : 'home';

  // Jumps straight to any page in the portal, including nested Event
  // Management sub/sub-sections — used by both the sidebar search and the
  // logo/"Admin" header (which goes home, i.e. clears all three).
  const goTo = ({ activePage: ap, eventSection: es = null, regSection: rs = null }) => {
    setActivePage(ap);
    setEventSection(es);
    setRegSection(rs);
    // Every nav action (sub-nav clicks, search, Quick Links, the Command
    // Palette, Home tiles) funnels through here, so this one line is enough
    // to auto-close the mobile drawer after picking a page.
    if (isMobile) setDrawerOpen(false);
  };
  // Dual-privilege/superuser users have an actual Home to return to; a
  // single-privilege user has no Home page, so the logo instead resets back
  // to that one category's root.
  const goHome = () => goTo({ activePage: defaultPage });

  useEffect(() => {
    adminClient.getCurrentUser().then(setUser);
  }, []);

  // Persists on every navigation, however it happened (goTo, the breadcrumb
  // "back" buttons that set eventSection/regSection directly, or the
  // privilege-redirect effect below) — watching the state itself instead of
  // wrapping goTo means nothing can bypass it.
  useEffect(() => {
    localStorage.setItem(ADMIN_NAV_STORAGE_KEY, JSON.stringify({ activePage, eventSection, regSection }));
  }, [activePage, eventSection, regSection]);

  useEffect(() => {
    localStorage.setItem(PINNED_PAGES_STORAGE_KEY, JSON.stringify(pinnedPages));
  }, [pinnedPages]);

  const togglePinned = (id) => {
    setPinnedPages(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  // Global Ctrl+K / Cmd+K — opens the command palette from anywhere in the
  // portal, since this component (and this listener) is always mounted.
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Single-privilege users skip the Home tile grid entirely and land
  // straight in their one category, the moment we know which one that is.
  // Also moves anyone off a page they can't open — e.g. one restored from
  // another user's session in the same browser.
  useEffect(() => {
    if (!user) return;
    const allowed = {
      home: hasBothAreas,
      schoolAccounts: hasAccountAdmin,
      userAccounts: hasAccountAdmin,
      events: hasEventView,
      workshops: hasWorkshopArea,
    };
    setActivePage(prev => (allowed[prev] ? prev : defaultPage));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const searchablePages = useMemo(() => buildSearchablePages(portals), [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const accountMenuOpen = Boolean(accountAnchor);
  const firstInitial = (user?.name || user?.email || '?').trim().charAt(0).toUpperCase();
  const savedThemeChoice = localStorage.getItem('themeMode') || 'system';
  // The theme's resolved mode, not the `mode` prop: that prop can be
  // 'system' (restored from localStorage), which the main panel's theme
  // resolves to the OS setting but SIDEBAR_PALETTE has no entry for — so the
  // sidebar and logo stayed light while the main panel went dark.
  const resolvedMode = useTheme().palette.mode;
  const sc = SIDEBAR_PALETTE[resolvedMode] || SIDEBAR_PALETTE.light;
  const logo = resolvedMode === 'dark' ? logoDark : logoLight;

  const handleOpenAccountMenu = (event) => {
    setAccountAnchor(event.currentTarget);
    setMenuView('main');
  };
  const handleCloseAccountMenu = () => {
    setAccountAnchor(null);
    setMenuView('main');
  };

  const applyThemeChoice = (choice) => {
    localStorage.setItem('themeMode', choice);
    const resolved = choice === 'system'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : choice;
    if (typeof setMode === 'function') {
      setMode(resolved);
    } else {
      applyCssVars(resolved);
    }
    handleCloseAccountMenu();
  };

  const handleLogout = () => adminClient.logout();

  const fetchPending = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSchools(await adminClient.getPendingSchools());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Guarded by hasAccountAdmin — these hit /api/admin/pending-schools and
  // /api/admin/approved-schools, which now require Account Admin, so an
  // Event-Admin-only user would otherwise get a stray 403 error alert on
  // mount for a page they never asked to see.
  useEffect(() => { if (hasAccountAdmin) fetchPending(); }, [fetchPending, hasAccountAdmin]);

  const fetchApproved = useCallback(async () => {
    setApprovedLoading(true);
    try {
      setApprovedSchools(await adminClient.getApprovedSchools());
    } catch (err) {
      setError(err.message);
    } finally {
      setApprovedLoading(false);
    }
  }, []);

  useEffect(() => { if (hasAccountAdmin) fetchApproved(); }, [fetchApproved, hasAccountAdmin]);

  // For a write whose exact result is already known (e.g. the contact-info
  // save below) — patches the one school in place instead of a full
  // fetchApproved() refetch. That refetch flips approvedLoading back to
  // true, which swaps the whole Approved Schools list out for a centered
  // spinner and back — collapsing the page's height for a moment and
  // resetting scroll position to the top in the process. A local patch has
  // no such flicker, since nothing ever unmounts.
  const patchApprovedSchool = useCallback((schoolId, fields) => {
    setApprovedSchools((prev) => prev.map((s) => (s.schoolId === schoolId ? { ...s, ...fields } : s)));
  }, []);

  const handleResetPassword = async (school) => {
    setResetting(school.schoolId);
    try {
      const data = await adminClient.regeneratePassword(school.schoolId);
      setCredentials({ schoolName: school.schoolName, schoolId: data.schoolId, password: data.password, mode: 'reset' });
    } catch (err) {
      setError(`Failed to reset password for ${school.schoolName}: ${err.message}`);
    } finally {
      setResetting(null);
    }
  };

  const handleApprove = async (school) => {
    setApproving(school.rowIndex);
    try {
      const data = await adminClient.approveSchool(school.rowIndex);
      setApprovedIds(prev => ({ ...prev, [school.rowIndex]: data.schoolId }));
      setCredentials({ schoolName: school.schoolName, schoolId: data.schoolId, password: data.password });
      setSchools(prev => prev.filter(s => s.rowIndex !== school.rowIndex));
      fetchApproved();
    } catch (err) {
      setError(`Failed to approve ${school.schoolName}: ${err.message}`);
    } finally {
      setApproving(null);
    }
  };

  return (
    // 100dvh, not 100vh: mobile Safari's address bar + bottom toolbar
    // slide in/out as you scroll, and 100vh is fixed to the viewport height
    // with that chrome hidden — see TeacherApp.js's MobileTopBar comment
    // for the full explanation. Applied here too since this layout hits the
    // identical bug once given a mobile view.
    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', height: '100dvh', overflow: 'hidden', bgcolor: 'background.default' }}>
      {/* Sidebar — a permanently docked panel on desktop, a collapsible
          slide-over Drawer on mobile (opened via the hamburger button in
          the mobile-only AppBar below). Drawer hard-codes position:'fixed'
          on its Paper for every variant including 'permanent', so the
          position override below is required, not cosmetic — without it
          the docked desktop sidebar would render as a fixed overlay with
          no width reserved for it in the flex layout. */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? drawerOpen : true}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isMobile ? undefined : 220,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isMobile ? 280 : 220,
            boxSizing: 'border-box',
            position: isMobile ? 'fixed' : 'relative',
            display: 'flex', flexDirection: 'column',
            bgcolor: sc.bg, color: sc.text,
            height: '100dvh',
            minHeight: 0,
            overflowY: 'auto',
            borderRight: '1px solid', borderColor: sc.border,
          },
        }}
      >
        {/* Logo */}
        <Box sx={{ p: '14px 16px', borderBottom: '1px solid', borderColor: sc.border, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={logo} alt="OSS Dancefest" style={{ width: 140, objectFit: 'contain' }} />
        </Box>

        {/* "Admin" — click to go home */}
        <Box
          onClick={goHome}
          sx={{
            p: 2, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
            borderBottom: '1px solid', borderColor: sc.border,
            bgcolor: activePage === 'home' ? sc.selectedBg : 'transparent',
            '&:hover': { bgcolor: activePage === 'home' ? sc.selectedBgHover : sc.hover },
          }}
        >
          <AdminPanelSettingsIcon sx={{ color: sc.accent }} />
          <Typography fontWeight={700} fontSize={15} color={sc.text}>Admin Home</Typography>
        </Box>

        {/* Search — jumps straight to any page, including nested Event Management
            sections. Also reachable instantly from anywhere via Ctrl+K (see the
            CommandPalette rendered at the bottom of this component). */}
        <Box sx={{ px: 1.5, pt: 1.5 }}>
          <Autocomplete
            size="small"
            options={searchablePages}
            getOptionLabel={(opt) => opt.label}
            groupBy={(opt) => opt.group}
            inputValue={searchInput}
            onInputChange={(e, value, reason) => { if (reason !== 'reset') setSearchInput(value); }}
            value={null}
            onChange={(e, value) => { if (value) { goTo(value.target); setSearchInput(''); } }}
            blurOnSelect
            renderOption={(props, option) => {
              const pinned = pinnedPages.includes(option.id);
              return (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>{option.label}</span>
                  <IconButton
                    size="small"
                    aria-label={pinned ? 'Unpin' : 'Pin to Quick Links'}
                    onClick={(e) => { e.stopPropagation(); togglePinned(option.id); }}
                  >
                    {pinned ? <PushPinIcon fontSize="small" color="primary" /> : <PushPinOutlinedIcon fontSize="small" />}
                  </IconButton>
                </Box>
              );
            }}
            sx={{
              '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': { color: sc.textFaint },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Jump to any page… (Ctrl+K)"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: sc.textFaint }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  // theme.js gives every MuiOutlinedInput an opaque
                  // --color-surface background globally (white in light
                  // mode) — that's a "white box" regardless of sidebar mode,
                  // and what made white input text disappear before this was
                  // theme-aware. Overriding it back to the sidebar's own colors.
                  '& .MuiOutlinedInput-root': { backgroundColor: sc.inputBg },
                  // Targeting the input element itself (not just the outlined
                  // container) — the container's color doesn't cascade to the
                  // native <input>, which is why typed text was invisible.
                  '& .MuiOutlinedInput-input': { color: sc.text, fontSize: 13 },
                  '& .MuiOutlinedInput-input::placeholder': { color: sc.textFaint, opacity: 1 },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: sc.inputBorder },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: sc.inputBorderHover },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: sc.accent },
                }}
              />
            )}
          />
        </Box>

        {/* Quick Links — pages pinned via the search results' pin icon (or the
            breadcrumb's pin toggle), always visible regardless of what page
            you're currently on. Stale entries (e.g. a privilege was revoked)
            are silently dropped rather than shown broken. */}
        {pinnedPages.length > 0 && (
          <Box sx={{ px: 1.5, pt: 1.5 }}>
            <Typography variant="caption" fontWeight={700} sx={{ color: sc.textFaint, textTransform: 'uppercase', letterSpacing: 0.5, pl: 1 }}>
              Quick Links
            </Typography>
            <List dense sx={{ py: 0.5 }}>
              {pinnedPages
                .map(id => searchablePages.find(p => p.id === id))
                .filter(Boolean)
                .map(page => (
                  <ListItemButton
                    key={page.id}
                    onClick={() => goTo(page.target)}
                    sx={{
                      mx: 0, borderRadius: 1, mb: 0.25, py: 0.5,
                      color: sc.textMuted,
                      '&:hover': { bgcolor: sc.hover },
                    }}
                  >
                    <ListItemText primary={page.label} primaryTypographyProps={{ fontSize: 13 }} />
                    <IconButton
                      size="small"
                      aria-label="Unpin"
                      onClick={(e) => { e.stopPropagation(); togglePinned(page.id); }}
                    >
                      <PushPinIcon fontSize="small" color="primary" />
                    </IconButton>
                  </ListItemButton>
                ))}
            </List>
          </Box>
        )}

        {/* Sidebar sub-nav — shows the *current* top-level category's
            subpages directly (no more persistent Event/Account Management
            list): Home has no sub-list at all, the two categories are picked
            via the Home tile grid instead. */}
        <List dense sx={{ pt: 1, flex: 1 }}>
          {(activePage === 'schoolAccounts' || activePage === 'userAccounts') && hasAccountAdmin && (
            [
              { id: 'schoolAccounts', label: 'School Accounts', icon: <SchoolIcon fontSize="small" /> },
              { id: 'userAccounts', label: 'User Accounts', icon: <AdminPanelSettingsIcon fontSize="small" /> },
            ].map(({ id, label, icon }) => (
              <ListItemButton
                key={id}
                selected={activePage === id}
                onClick={() => goTo({ activePage: id })}
                sx={{
                  mx: 1, borderRadius: 1, mb: 0.5,
                  color: activePage === id ? sc.text : sc.textMuted,
                  bgcolor: activePage === id ? sc.selectedBg : 'transparent',
                  '&:hover': { bgcolor: sc.hover },
                  '&.Mui-selected': { bgcolor: sc.selectedBg },
                  '&.Mui-selected:hover': { bgcolor: sc.selectedBgHover },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>{icon}</ListItemIcon>
                <ListItemText primary={label} primaryTypographyProps={{ fontSize: 13 }} />
              </ListItemButton>
            ))
          )}
          {activePage === 'workshops' && hasWorkshopArea && WORKSHOP_SUBSECTIONS.map(({ id, label }) => (
            <ListItemButton
              key={id}
              selected={regSection === id}
              onClick={() => goTo({ activePage: 'workshops', regSection: id })}
              sx={{
                mx: 1, borderRadius: 1, mb: 0.5,
                color: regSection === id ? sc.text : sc.textMuted,
                bgcolor: regSection === id ? sc.selectedBg : 'transparent',
                '&:hover': { bgcolor: sc.hover },
                '&.Mui-selected': { bgcolor: sc.selectedBg },
                '&.Mui-selected:hover': { bgcolor: sc.selectedBgHover },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}><SelfImprovementIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary={label} primaryTypographyProps={{ fontSize: 13 }} />
            </ListItemButton>
          ))}
          {activePage === 'events' && hasEventView && EVENT_SECTIONS.map(({ id, label, icon: Icon }) => (
            <ListItemButton
              key={id}
              selected={eventSection === id}
              onClick={() => goTo({ activePage: 'events', eventSection: id })}
              sx={{
                mx: 1, borderRadius: 1, mb: 0.5,
                color: eventSection === id ? sc.text : sc.textMuted,
                bgcolor: eventSection === id ? sc.selectedBg : 'transparent',
                '&:hover': { bgcolor: sc.hover },
                '&.Mui-selected': { bgcolor: sc.selectedBg },
                '&.Mui-selected:hover': { bgcolor: sc.selectedBgHover },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}><Icon fontSize="small" /></ListItemIcon>
              <ListItemText primary={label} primaryTypographyProps={{ fontSize: 13 }} />
            </ListItemButton>
          ))}
        </List>
        <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: sc.border }}>
          <Box
            onClick={handleOpenAccountMenu}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.25, p: 1, borderRadius: 1,
              cursor: 'pointer', '&:hover': { bgcolor: sc.hover },
            }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14, fontWeight: 700 }}>
              {firstInitial}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography noWrap fontSize={13} fontWeight={600} color={sc.text}>
                {user?.name || 'Admin'}
              </Typography>
              <Typography noWrap fontSize={11} color={sc.textFaint}>
                {user?.email || ''}
              </Typography>
            </Box>
          </Box>

          <Menu
            anchorEl={accountAnchor}
            open={accountMenuOpen}
            onClose={handleCloseAccountMenu}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            {menuView === 'main' ? (
              <div>
                <MenuItem onClick={() => setMenuView('theme')}>
                  <ListItemIcon><PaletteIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Theme</ListItemText>
                  <ListItemIcon sx={{ minWidth: 24, justifyContent: 'flex-end' }}>
                    <ChevronRightIcon fontSize="small" />
                  </ListItemIcon>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </div>
            ) : (
              <div>
                <MenuItem onClick={() => setMenuView('main')}>
                  <ListItemIcon><ArrowBackIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Theme</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => applyThemeChoice('light')}>
                  <ListItemIcon><LightModeIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Light Mode</ListItemText>
                  {savedThemeChoice === 'light' && <CheckIcon fontSize="small" />}
                </MenuItem>
                <MenuItem onClick={() => applyThemeChoice('dark')}>
                  <ListItemIcon><DarkModeIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Dark Mode</ListItemText>
                  {savedThemeChoice === 'dark' && <CheckIcon fontSize="small" />}
                </MenuItem>
                <MenuItem onClick={() => applyThemeChoice('system')}>
                  <ListItemIcon><SettingsBrightnessIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Use System Settings</ListItemText>
                  {savedThemeChoice === 'system' && <CheckIcon fontSize="small" />}
                </MenuItem>
              </div>
            )}
          </Menu>
        </Box>
      </Drawer>

      {/* Mobile-only top bar — hamburger opens the Drawer above. Kept
          deliberately minimal (just the hamburger + logo); theme/logout
          stay inside the drawer, one tap away, rather than being
          duplicated into a second surface that would need to track the
          drawer's own menuView state machine. */}
      {isMobile && (
        <AppBar position="static" elevation={0} sx={{ bgcolor: sc.bg, color: sc.text, borderBottom: '1px solid', borderColor: sc.border, flexShrink: 0 }}>
          <Toolbar sx={{ gap: 1.5, minHeight: 56 }}>
            <IconButton edge="start" onClick={() => setDrawerOpen(true)} sx={{ color: sc.text }} aria-label="Open menu">
              <MenuIcon />
            </IconButton>
            <img src={logo} alt="OSS Dancefest" style={{ height: 32, width: 'auto', objectFit: 'contain' }} />
          </Toolbar>
        </AppBar>
      )}

      {/* Main content — the only panel that scrolls. minHeight: 0 overrides the
          flex item's default content-based min-height, which otherwise lets
          it grow past 100dvh instead of clipping/scrolling internally. */}
      <Box sx={{ flex: 1, p: isMobile ? 2 : 4, overflowY: 'auto', height: '100dvh', minHeight: 0 }}>
        <Box>
          <AdminBreadcrumbs
            activePage={activePage} goHome={hasBothAreas ? goHome : null}
            eventSection={eventSection} setEventSection={setEventSection}
            regSection={regSection} setRegSection={setRegSection}
            pinnedPages={pinnedPages} togglePinned={togglePinned}
          />
          {/* Defensive fallback — ProtectedRoute already keeps a user with
              neither privilege out of /admin entirely, but guard against a
              transient empty portals list (e.g. mid cache-refresh) rather
              than rendering a blank sidebar/panel. */}
          {user && !hasAccountAdmin && !hasEventView && !hasWorkshopArea && (
            <Alert severity="warning">You don't have access to any admin section.</Alert>
          )}
          {activePage === 'home' && <AdminHome onNavigate={(id) => goTo({ activePage: id })} areas={{ events: hasEventView, schoolAccounts: hasAccountAdmin, workshops: hasWorkshopArea }} />}
          {activePage === 'schoolAccounts' && <SchoolManagement
            schools={schools} loading={loading} error={error} approving={approving}
            approvedIds={approvedIds} expanded={expanded} toggleExpanded={toggleExpanded}
            approvedSchools={approvedSchools} approvedLoading={approvedLoading} resetting={resetting}
            handleApprove={handleApprove} handleResetPassword={handleResetPassword}
            fetchPending={fetchPending} fetchApproved={fetchApproved} patchApprovedSchool={patchApprovedSchool}
            setError={setError}
          />}
          {activePage === 'userAccounts' && <UserManagementSection />}
          {activePage === 'events' && hasEventView && (
            <EventReadOnlyContext.Provider value={eventReadOnly}>
              {(eventSection === 'workshops' ? workshopReadOnly : eventReadOnly) && <ViewOnlyNote />}
              <EventInformation
                activeSection={eventSection} setActiveSection={setEventSection}
                regSection={regSection} setRegSection={setRegSection}
                canBoth={hasBoth} workshopReadOnly={workshopReadOnly}
              />
            </EventReadOnlyContext.Provider>
          )}
          {activePage === 'workshops' && hasWorkshopArea && (
            <EventReadOnlyContext.Provider value={workshopReadOnly}>
              {workshopReadOnly && <ViewOnlyNote />}
              <WorkshopsSection activeSection={regSection} setActiveSection={setRegSection} />
            </EventReadOnlyContext.Provider>
          )}
          {/* Bottom padding on a scrolling container is unreliable — many
              browsers clip it once content overflows, so the last row of a
              long table ends up flush against the window edge. An explicit
              spacer as the last scrollable child reserves the space for real. */}
          <Box sx={{ height: 40 }} />
        </Box>
      </Box>

      <CommandPalette
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        pages={searchablePages}
        onNavigate={goTo}
      />

      {/* Credentials dialog — rendered at top level so it works from any page */}
      <Dialog open={credentials?.mode === 'reset'} onClose={() => setCredentials(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" />
          {credentials?.mode === 'reset' ? 'New Password Generated' : 'School Approved'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {credentials?.mode === 'reset'
              ? <>Copy the new password and send it to <strong>{credentials?.schoolName}</strong>. The old password is now invalid.</>
              : <>Copy these credentials and send them to <strong>{credentials?.schoolName}</strong>.</>
            }
            {' '}The password cannot be retrieved again after closing this dialog.
          </Typography>

          {[{ label: 'School ID', key: 'schoolId' }, { label: 'Password', key: 'password' }].map(({ label, key }) => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography
                  fontFamily="monospace"
                  fontSize={15}
                  sx={{ bgcolor: 'action.hover', px: 1.5, py: 0.5, borderRadius: 1 }}
                >
                  {credentials?.[key]}
                </Typography>
              </Box>
              <Tooltip title={copied === key ? 'Copied!' : 'Copy'} placement="top">
                <IconButton
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(credentials?.[key] || '');
                    setCopied(key);
                    setTimeout(() => setCopied(null), 2000);
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCredentials(null); setCopied(null); }} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Teachers enter their cell phone in whatever format they like on the
// original registration form ("416-555-1234", "(416) 555.1234", "4165551234",
// "416-555-1234 x56", "(416) 555.1234 ext. 56", etc.) — strip everything but
// digits and rebuild it as "###-###-####" (plus " x ####" for an extension,
// kept at whatever length it was given) so it always displays the same way
// here regardless of how it was typed. An extension is recognized by an "x"
// or "ext"/"ext." marker (most teachers just use x/X) followed by digits;
// it's split off BEFORE stripping digits from the rest, so its digits never
// get merged into the main number. Tolerates a leading "1" country code on
// the main number. Anything that doesn't resolve to exactly 10 main digits
// (incomplete, non-North-American) is left exactly as entered rather than
// mangled.
function formatPhoneNumber(value) {
  if (!value) return value;
  const str = String(value);
  const extMatch = str.match(/^(.*?)\s*(?:ext\.?|x)\s*[:.]?\s*(\d+)\s*$/i);
  const mainPart = extMatch ? extMatch[1] : str;
  const extension = extMatch ? extMatch[2] : '';

  const digits = mainPart.replace(/\D/g, '');
  const tenDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (tenDigits.length !== 10) return value;

  const formatted = `${tenDigits.slice(0, 3)}-${tenDigits.slice(3, 6)}-${tenDigits.slice(6)}`;
  return extension ? `${formatted} x ${extension}` : formatted;
}

function SchoolManagement({
  schools, loading, error, approving, approvedIds, expanded, toggleExpanded,
  approvedSchools, approvedLoading, resetting,
  handleApprove, handleResetPassword, fetchPending, fetchApproved, patchApprovedSchool, setError,
}) {
  // Primary-contact editing lives right in each Approved school's subtitle
  // line (see the map() below) rather than as its own section in
  // SchoolDetails — editingContactId tracks which one card (by schoolId) is
  // currently in edit mode; only one at a time.
  const [editingContactId, setEditingContactId] = useState(null);
  const [contactForm, setContactForm] = useState({ registrantName: '', registrantEmail: '' });
  const [savingContactId, setSavingContactId] = useState(null);
  const [contactError, setContactError] = useState(null);

  // School-name editing — same one-card-at-a-time pattern, shown once the
  // card's details are open. A rename also rewrites the name on the school's
  // event registration, entries, payments and nomination (they link by name
  // — see PUT /api/admin/approved-schools/:schoolId/name), hence the confirm.
  const [editingNameId, setEditingNameId] = useState(null);
  const [nameDraft, setNameDraft] = useState('');
  const [savingNameId, setSavingNameId] = useState(null);
  const [nameError, setNameError] = useState(null);
  const [confirmRename, setConfirmRename] = useState(null); // school pending confirmation

  const startEditingName = (school) => {
    setNameDraft(school.schoolName);
    setNameError(null);
    setEditingNameId(school.schoolId);
  };

  const handleRename = async (school) => {
    setConfirmRename(null);
    setSavingNameId(school.schoolId);
    setNameError(null);
    try {
      const { schoolName } = await adminClient.renameSchool(school.schoolId, nameDraft);
      // Patch in place for the same no-flicker reason as handleSaveContact.
      patchApprovedSchool(school.schoolId, { schoolName });
      setEditingNameId(null);
    } catch (err) {
      setNameError(err.message);
    } finally {
      setSavingNameId(null);
    }
  };

  const startEditingContact = (school) => {
    setContactForm({ registrantName: school.registrantName, registrantEmail: school.registrantEmail });
    setContactError(null);
    setEditingContactId(school.schoolId);
  };

  const handleSaveContact = async (school) => {
    setSavingContactId(school.schoolId);
    setContactError(null);
    try {
      await adminClient.updateSchoolContact(school.schoolId, contactForm);
      // Patch in place rather than fetchApproved() — a full refetch flips
      // approvedLoading back to true, which swaps the whole list out for a
      // centered spinner and back, collapsing the page's height for a
      // moment and resetting scroll to the top. We already know the exact
      // new values, so there's nothing a refetch would tell us that we
      // don't already have.
      patchApprovedSchool(school.schoolId, contactForm);
      setEditingContactId(null);
    } catch (err) {
      setContactError(err.message);
    } finally {
      setSavingContactId(null);
    }
  };

  return (
    <>
      <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SchoolIcon fontSize="small" /> Pending School Registrations
        <Button variant="text" size="small" onClick={fetchPending} sx={{ mt: 0.5 }}>Refresh</Button>
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : schools.length === 0 ? (
        <Typography color="text.secondary">No pending registrations.</Typography>
      ) : (
        <Stack spacing={2}>
          {schools.map((school) => {
            const justApproved = approvedIds[school.rowIndex];
            return (
              <Card key={school.rowIndex} elevation={2}>
                <CardContent sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={600}>{school.schoolName}</Typography>
                    <Typography variant="body2" color="text.secondary">{school.schoolAddress}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {school.registrantName} · {school.registrantEmail}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">{school.timestamp}</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Button size="small" variant="text" sx={{ p: 0, minWidth: 0, fontSize: 12 }}
                        onClick={() => toggleExpanded(`p-${school.rowIndex}`)}
                        endIcon={expanded[`p-${school.rowIndex}`] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      >
                        {expanded[`p-${school.rowIndex}`] ? 'Hide details' : 'View details'}
                      </Button>
                    </Box>
                    <Collapse in={!!expanded[`p-${school.rowIndex}`]}>
                      <SchoolDetails school={school} />
                    </Collapse>
                    {justApproved && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircleIcon fontSize="small" color="success" />
                        <Typography variant="body2" color="success.main">
                          Approved — School ID: <strong>{justApproved}</strong>
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {!justApproved && (
                    <Button variant="contained" size="small" disabled={approving === school.rowIndex}
                      onClick={() => handleApprove(school)} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {approving === school.rowIndex ? <CircularProgress size={18} color="inherit" /> : 'Approve'}
                    </Button>
                  )}
                  {justApproved && <Chip label="Approved" color="success" size="small" />}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 6 }}>
        <CheckCircleIcon fontSize="small" color="success" /> Approved Schools
        <Button variant="text" size="small" onClick={fetchApproved} sx={{ mt: 0.5 }}>Refresh</Button>
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {approvedLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : approvedSchools.length === 0 ? (
        <Typography color="text.secondary">No approved schools yet.</Typography>
      ) : (
        <Stack spacing={2}>
          {[...approvedSchools].sort((a, b) => a.schoolName.localeCompare(b.schoolName)).map((school) => (
            <Card key={school.schoolId} elevation={1}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {expanded[`a-${school.schoolId}`] && editingNameId === school.schoolId ? (
                        <>
                          <TextField
                            size="small" variant="standard" label="School Name" autoFocus
                            value={nameDraft}
                            onChange={(e) => setNameDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && nameDraft.trim() && nameDraft.trim() !== school.schoolName) setConfirmRename(school);
                              if (e.key === 'Escape') setEditingNameId(null);
                            }}
                            sx={{ minWidth: 260 }}
                          />
                          <Tooltip title="Save name">
                            <span>
                              <IconButton size="small" aria-label="Save school name"
                                disabled={savingNameId === school.schoolId || !nameDraft.trim() || nameDraft.trim() === school.schoolName}
                                onClick={() => setConfirmRename(school)}>
                                {savingNameId === school.schoolId ? <CircularProgress size={14} /> : <SaveIcon sx={{ fontSize: 16 }} />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Button size="small" onClick={() => setEditingNameId(null)} disabled={savingNameId === school.schoolId}>Cancel</Button>
                        </>
                      ) : (
                        <>
                          <Typography fontWeight={600}>{school.schoolName}</Typography>
                          {expanded[`a-${school.schoolId}`] && (
                            <Tooltip title="Rename school">
                              <IconButton size="small" aria-label="Rename school" onClick={() => startEditingName(school)} sx={{ p: 0.25 }}>
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                      {school.isTest && <TestSchoolChip sx={{ ml: 0 }} />}
                      {school.schoolBoard && (
                        <Typography variant="caption" color="text.secondary">· {school.schoolBoard}</Typography>
                      )}
                    </Box>
                    {editingNameId === school.schoolId && nameError && (
                      <Typography variant="caption" color="error" display="block">{nameError}</Typography>
                    )}
                    {(() => {
                      const isOpen = !!expanded[`a-${school.schoolId}`];
                      const isEditing = isOpen && editingContactId === school.schoolId;
                      const isSaving = savingContactId === school.schoolId;
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5 }}>
                          {isOpen && (
                            <IconButton
                              size="small"
                              onClick={() => isEditing ? handleSaveContact(school) : startEditingContact(school)}
                              disabled={isSaving}
                              sx={{ p: 0.25, mt: '-2px' }}
                              aria-label={isEditing ? 'Save primary contact' : 'Edit primary contact'}
                            >
                              {isSaving ? <CircularProgress size={14} /> : isEditing ? <SaveIcon sx={{ fontSize: 16 }} /> : <EditIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          )}
                          {isEditing ? (
                            <Stack sx={{ flex: 1 }}>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <TextField
                                  size="small" variant="standard" label="Teacher Name"
                                  value={contactForm.registrantName}
                                  onChange={(e) => setContactForm((f) => ({ ...f, registrantName: e.target.value }))}
                                  sx={{ minWidth: 160 }}
                                />
                                <TextField
                                  size="small" variant="standard" label="Email" type="email"
                                  value={contactForm.registrantEmail}
                                  onChange={(e) => setContactForm((f) => ({ ...f, registrantEmail: e.target.value }))}
                                  sx={{ minWidth: 220 }}
                                />
                              </Stack>
                              {contactError && <Typography variant="caption" color="error">{contactError}</Typography>}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {[school.registrantName, school.registrantEmail, school.alternateEmail, school.cellPhone ? `${formatPhoneNumber(school.cellPhone)}${school.position ? ` (${school.position})` : ''}` : school.position ? `(${school.position})` : ''].filter(Boolean).join(' · ')}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>
                  <Button size="small" variant="text" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                    onClick={() => toggleExpanded(`a-${school.schoolId}`)}
                    endIcon={expanded[`a-${school.schoolId}`] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  >
                    {expanded[`a-${school.schoolId}`] ? 'Hide' : 'Details'}
                  </Button>
                </Box>
                <Collapse in={!!expanded[`a-${school.schoolId}`]}>
                  <SchoolDetails
                    school={school}
                    onReset={() => handleResetPassword(school)}
                    resetting={resetting === school.schoolId}
                  />
                </Collapse>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={!!confirmRename} onClose={() => setConfirmRename(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename school?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            <strong>{confirmRename?.schoolName}</strong> will be renamed to <strong>{nameDraft.trim()}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The new name is also applied to this school's event registration, entries, payments and
            Most Inspiring Dancer nomination, so everything stays linked. The school's login ID doesn't change.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRename(null)}>Cancel</Button>
          <Button variant="contained" onClick={() => handleRename(confirmRename)}>Rename</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function ViewOnlyNote() {
  return (
    <Alert severity="info" icon={<VisibilityIcon fontSize="inherit" />} sx={{ mb: 2 }}>
      View only — you can see everything here, but not make changes.
    </Alert>
  );
}

function AdminBreadcrumbs({ activePage, goHome, eventSection, setEventSection, regSection, setRegSection, pinnedPages, togglePinned }) {
  if (activePage === 'home') return null;

  const crumbs = [];

  if (activePage === 'schoolAccounts' || activePage === 'userAccounts') {
    // No separate Account Management landing page to go "back" to — the
    // root crumb only does something for dual-privilege users, jumping Home.
    crumbs.push({ label: 'Account Management', onClick: goHome || null });
    crumbs.push({ label: activePage === 'schoolAccounts' ? 'School Accounts' : 'User Accounts' });
  } else if (activePage === 'workshops') {
    crumbs.push({ label: 'Workshops', onClick: goHome || null });
    const subLabel = regSection && WORKSHOP_SUBSECTIONS.find(s => s.id === regSection)?.label;
    if (subLabel) crumbs.push({ label: subLabel });
  } else {
    const atRoot = !eventSection && !regSection;
    crumbs.push({
      label: 'Event Management',
      onClick: atRoot ? (goHome || null) : () => { setEventSection(null); setRegSection(null); },
    });
    if (eventSection) {
      // Sections with sub-pages switch between them with tabs (SectionTabs),
      // so the section crumb is just a label: Section › Tab.
      const sectionLabel = EVENT_SECTIONS.find(s => s.id === eventSection)?.label ?? eventSection;
      crumbs.push({ label: sectionLabel });
      // regSection is the tab for Registration, Workshops and Awards. Only
      // show a crumb for one this section actually has, so a stale id from
      // another section doesn't add one.
      const subsections = eventSection === 'awards' ? AWARDS_SUBSECTIONS : eventSection === 'workshops' ? WORKSHOP_SUBSECTIONS : REG_SUBSECTIONS;
      const subLabel = regSection && subsections.find(s => s.id === regSection)?.label;
      if (subLabel) crumbs.push({ label: subLabel });
    }
  }

  const currentPageId = pageId({ activePage, eventSection, regSection });
  const pinned = pinnedPages?.includes(currentPageId);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
      <Breadcrumbs separator={<NavigateNextIcon sx={{ fontSize: 16 }} />}>
        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1;
          if (crumb.onClick) {
            return (
              <Link key={i} component="button" variant="body2" color="text.secondary"
                underline="hover" onClick={crumb.onClick} sx={{ cursor: 'pointer' }}>
                {crumb.label}
              </Link>
            );
          }
          return (
            <Typography key={i} variant="body2" color={isLast ? 'text.primary' : 'text.secondary'}
              fontWeight={isLast ? 600 : 400}>
              {crumb.label}
            </Typography>
          );
        })}
      </Breadcrumbs>
      {togglePinned && (
        <Tooltip title={pinned ? 'Unpin from Quick Links' : 'Pin to Quick Links'}>
          <IconButton size="small" onClick={() => togglePinned(currentPageId)}>
            {pinned ? <PushPinIcon fontSize="small" color="primary" /> : <PushPinOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

// Ctrl+K / Cmd+K overlay — same underlying page list as the sidebar search
// (searchablePages), just reachable instantly from any page instead of only
// via the sidebar. Enter jumps to the top match; Escape/backdrop-click closes
// (handled by MUI Dialog's own onClose, no extra listener needed).
function CommandPalette({ open, onClose, pages, onNavigate }) {
  const [query, setQuery] = useState('');
  useEffect(() => { if (open) setQuery(''); }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q ? pages.filter(p => p.label.toLowerCase().includes(q)) : pages;

  const handleSelect = (page) => {
    onNavigate(page.target);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { position: 'fixed', top: 96, m: 0 } }}>
      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Jump to a page…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && filtered.length > 0) handleSelect(filtered[0]); }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
      </Box>
      <List dense sx={{ maxHeight: 320, overflowY: 'auto', py: 0 }}>
        {filtered.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>No matching pages.</Typography>
        )}
        {filtered.map((page, i) => (
          <ListItemButton key={page.id} onClick={() => handleSelect(page)} selected={i === 0}>
            <ListItemText
              primary={page.label}
              secondary={page.group}
              primaryTypographyProps={{ fontSize: 14 }}
              secondaryTypographyProps={{ fontSize: 11 }}
            />
          </ListItemButton>
        ))}
      </List>
    </Dialog>
  );
}

function fmtDate(iso) {
  if (!iso) return null;
  // parseLocalDate avoids new Date('YYYY-MM-DD')'s UTC-midnight parsing,
  // which rolls the displayed date back a day in any timezone behind UTC
  // (i.e. everywhere in North America) — see its own comment for why.
  const d = parseLocalDate(iso);
  return d ? d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : iso;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Converts a date string to the yyyy-mm-dd shape a <TextField type="date">
// requires. Handles both an already-ISO string and the "Month D, YYYY" (or
// "Weekday, Month D, YYYY") form Google Sheets renders date-typed cells as.
// Deliberately avoids toISOString()/UTC round-tripping — parsing "yyyy-mm-dd"
// with the Date constructor treats it as UTC midnight, and reading it back
// with local getters can land on the previous day in timezones behind UTC.
function toDateInputValue(str) {
  if (!str) return '';
  const trimmed = str.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const commaIdx = trimmed.indexOf(', ');
  const withoutWeekday = commaIdx !== -1 && WEEKDAYS.includes(trimmed.slice(0, commaIdx))
    ? trimmed.slice(commaIdx + 2)
    : trimmed;
  const d = new Date(withoutWeekday);
  if (isNaN(d)) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

const EMPTY_EVENT_FORM = {
  eventName: '', location: '', address: '', startDate: '', endDate: '', extraInfo: '', infoDocUrl: '',
  maxParticipants: '', maxEntries: '', registrationOverride: '', participantsLockOverride: '', entriesLockOverride: '', isCurrent: false,
};

// '' | 'Locked' | 'Open' everywhere overrides are used — same options, same
// meaning (defer to automatic behavior / force closed / force open).
const OVERRIDE_OPTIONS = [
  { value: '', label: 'Auto' },
  { value: 'Locked', label: 'Locked' },
  { value: 'Open', label: 'Open' },
];

const EVENT_SECTIONS = [
  { id: 'details', label: 'Event Details', icon: LocationOnIcon, description: 'Location, dates, timeline, and prices' },
  { id: 'registration', label: 'Registration', icon: AppRegistrationIcon, description: 'School, participant, and entry registrations' },
  { id: 'workshops', label: 'Workshops', icon: SelfImprovementIcon, description: 'Workshop schedule and sign-ups' },
  { id: 'program', label: 'Program', icon: MenuBookIcon, description: 'Performance order and program content' },
  { id: 'awards', label: 'Awards', icon: EmojiEventsIcon, description: 'Categories, scoring, and results' },
];

// Stable id for a page target — used as the React key, the search result
// key, and the localStorage-persisted pin key, so pinning survives a page's
// label text changing and doesn't depend on array position.
function pageId({ activePage, eventSection, regSection }) {
  return [activePage, eventSection || '', regSection || ''].join('|');
}

// Flat registry for the sidebar search — built from the same EVENT_SECTIONS/
// *_SUBSECTIONS arrays that drive the sidebar and section tabs, filtered by the
// current user's privileges so search can't surface a page they can't reach.
function buildSearchablePages(portals) {
  const hasAccountAdmin = portals.includes('accountAdmin');
  const hasEventAdmin = portals.includes('eventAdmin');
  const hasBoth = hasAccountAdmin && hasEventAdmin;
  // Event Viewers search the same Event Management pages (view only).
  const hasEventView = hasEventAdmin || portals.includes('eventViewer');
  const hasWorkshopArea = !hasEventView && (portals.includes('workshopAdmin') || portals.includes('workshopViewer'));
  const pages = [];
  if ([hasAccountAdmin, hasEventView, hasWorkshopArea].filter(Boolean).length > 1) {
    pages.push({ label: 'Home', group: 'Admin', target: { activePage: 'home' } });
  }
  if (hasWorkshopArea) {
    pages.push({ label: 'Workshops', group: 'Admin', target: { activePage: 'workshops' } });
    pages.push(...WORKSHOP_SUBSECTIONS.map(s => ({
      label: s.label, group: 'Workshops',
      target: { activePage: 'workshops', regSection: s.id },
    })));
  }
  if (hasAccountAdmin) {
    pages.push({ label: 'School Accounts', group: 'Account Management', target: { activePage: 'schoolAccounts' } });
    pages.push({ label: 'User Accounts', group: 'Account Management', target: { activePage: 'userAccounts' } });
  }
  if (hasEventView) {
    pages.push({ label: 'Event Management', group: 'Admin', target: { activePage: 'events' } });
    pages.push(...EVENT_SECTIONS.map(s => ({
      label: s.label, group: 'Event Management',
      target: { activePage: 'events', eventSection: s.id },
    })));
    // School Payments (fee/payment tracking) requires both privileges — see
    // EventRegistrationSection's own gate, which this must stay in sync with
    // so search/Quick Links can't surface a page the user can't open.
    pages.push(...REG_SUBSECTIONS.filter(s => s.id !== 'lookup' || hasBoth).map(s => ({
      label: s.label, group: 'Event Management › Registration',
      target: { activePage: 'events', eventSection: 'registration', regSection: s.id },
    })));
    pages.push(...AWARDS_SUBSECTIONS.map(s => ({
      label: s.label, group: 'Event Management › Awards',
      target: { activePage: 'events', eventSection: 'awards', regSection: s.id },
    })));
    pages.push(...WORKSHOP_SUBSECTIONS.map(s => ({
      label: s.label, group: 'Event Management › Workshops',
      target: { activePage: 'events', eventSection: 'workshops', regSection: s.id },
    })));
  }
  return pages.map(p => ({ ...p, id: pageId(p.target) }));
}

// Restores which event was selected across a browser refresh — rowIndex is
// positional (see the wider rowIndex discussion elsewhere in this app), but
// fetchEvents below already only trusts a "previously selected" id once
// it's confirmed to still exist in the freshly-fetched event list, so a
// stale/deleted event id here just falls through to the normal
// auto-select-if-only-one-or-null behavior rather than showing anything wrong.
const EVENT_ID_STORAGE_KEY = 'admin:selectedEventId';

function EventInformation({ activeSection, setActiveSection, regSection, setRegSection, canBoth, workshopReadOnly }) {
  const readOnly = useEventReadOnly();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(() => {
    const stored = localStorage.getItem(EVENT_ID_STORAGE_KEY);
    return stored ? Number(stored) : null;
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' | 'edit'
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [form, setForm] = useState(EMPTY_EVENT_FORM);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminClient.getEvents();
      setEvents(data);
      // Auto-select only when there's exactly one event — unambiguous, no
      // reason to make someone pick. With multiple events, don't guess;
      // require an explicit choice (see the "select an event" prompt below).
      setSelectedEventId(prev => {
        if (prev && data.some(e => e.rowIndex === prev)) return prev;
        return data.length === 1 ? data[0].rowIndex : null;
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  useEffect(() => {
    if (selectedEventId != null) {
      localStorage.setItem(EVENT_ID_STORAGE_KEY, String(selectedEventId));
    } else {
      localStorage.removeItem(EVENT_ID_STORAGE_KEY);
    }
  }, [selectedEventId]);

  const event = events.find(e => e.rowIndex === selectedEventId) || null;

  const openCreate = () => {
    setForm(EMPTY_EVENT_FORM);
    setFormError(null);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const openEditEvent = (ev) => {
    if (!ev) return;
    setForm({
      eventName: ev.eventName,
      location: ev.location,
      address: ev.address,
      startDate: toDateInputValue(ev.startDate),
      endDate: toDateInputValue(ev.endDate),
      extraInfo: ev.extraInfo || '',
      infoDocUrl: ev.infoDocUrl || '',
      maxParticipants: ev.maxParticipants || '',
      maxEntries: ev.maxEntries || '',
      registrationOverride: ev.registrationOverride || '',
      participantsLockOverride: ev.participantsLockOverride || '',
      entriesLockOverride: ev.entriesLockOverride || '',
      isCurrent: !!ev.isCurrent,
    });
    setFormError(null);
    setDialogMode('edit');
    setEditingRowIndex(ev.rowIndex);
    setDialogOpen(true);
  };

  // Lets the Capacity card's inline override dropdowns write a single field
  // without opening the full Edit Details dialog — the PUT route still wants
  // the whole event payload, so this rebuilds it from the current `event`
  // plus just the one changed field.
  const handleQuickUpdate = async (partial) => {
    if (!event) return;
    await adminClient.updateEvent(event.rowIndex, {
      eventName: event.eventName,
      location: event.location,
      address: event.address,
      startDate: toDateInputValue(event.startDate),
      endDate: toDateInputValue(event.endDate),
      extraInfo: event.extraInfo || '',
      infoDocUrl: event.infoDocUrl || '',
      maxParticipants: event.maxParticipants || '',
      maxEntries: event.maxEntries || '',
      registrationOverride: event.registrationOverride || '',
      participantsLockOverride: event.participantsLockOverride || '',
      entriesLockOverride: event.entriesLockOverride || '',
      isCurrent: !!event.isCurrent,
      ...partial,
    });
    await fetchEvents();
  };

  const handleSave = async () => {
    if (!form.eventName || !form.startDate || !form.endDate) {
      setFormError('Event name, start date, and end date are required.');
      return;
    }
    if (form.endDate < form.startDate) {
      setFormError('End date cannot be before start date.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const isEdit = dialogMode === 'edit';
      if (isEdit) {
        await adminClient.updateEvent(editingRowIndex, form);
      } else {
        await adminClient.createEvent(form);
      }
      setDialogOpen(false);
      await fetchEvents();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Shared between the landing page and every subsection, so switching
  // events works the same way no matter how deep you are (including inside
  // Registration's own Schools/Participants/Entries/Lookup sub-nav).
  const eventSelector = (
    <FormControl size="small" sx={{ minWidth: 220 }} disabled={events.length === 0}>
      <InputLabel>Event</InputLabel>
      <Select value={selectedEventId ?? ''} label="Event" onChange={e => setSelectedEventId(e.target.value)}>
        <MenuItem value="" disabled><em>Select an event…</em></MenuItem>
        {events.map(ev => (
          <MenuItem key={ev.rowIndex} value={ev.rowIndex}>{ev.eventName}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <>
      {activeSection ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 2 }}>
            {/* Printouts are all registration data (rosters, contacts,
                plaques), so Print lives in the Registration section only —
                on every Registration tab. */}
            {activeSection === 'registration' && (
              <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={() => setPrintOpen(true)}>
                Print…
              </Button>
            )}
            {eventSelector}
          </Box>
          <PrintDialog open={printOpen} onClose={() => setPrintOpen(false)} />
          {!event ? (
            // Reachable via the sidebar search jumping straight into a
            // subsection, bypassing the tile grid's own guard below.
            <Card variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <Typography fontWeight={500} gutterBottom>No event selected</Typography>
              <Typography variant="body2">
                {events.length === 0 ? 'Create an event first, from Event Management.' : 'Select an event above to continue.'}
              </Typography>
            </Card>
          ) : (
            <>
              {activeSection === 'details' && <EventDetailsSection event={event} onEdit={() => openEditEvent(event)} onQuickUpdate={handleQuickUpdate} />}
              {activeSection === 'registration' && (
                <EventRegistrationSection event={event} activeSection={regSection} setActiveSection={setRegSection} canBoth={canBoth} />
              )}
              {activeSection === 'workshops' && (
                // regSection doubles as the Workshops sub-page — see WorkshopsSection.
                // Read-only follows workshop access, not Event Management's.
                <EventReadOnlyContext.Provider value={workshopReadOnly}>
                  <WorkshopsSection activeSection={regSection} setActiveSection={setRegSection} />
                </EventReadOnlyContext.Provider>
              )}
              {activeSection === 'program' && <EventProgramSection event={event} />}
              {activeSection === 'awards' && (
                // regSection doubles as the Awards sub-page — see AwardsSection.
                <AwardsSection activeSection={regSection} setActiveSection={setRegSection} />
              )}
            </>
          )}
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventNoteIcon fontSize="small" /> Event Management
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {eventSelector}
              {!readOnly && (
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}>
                  New Event
                </Button>
              )}
            </Box>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : events.length === 0 ? (
            <Card variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <Typography fontWeight={500} gutterBottom>No events yet</Typography>
              <Typography variant="body2">Create your first event to start managing it.</Typography>
            </Card>
          ) : !event ? (
            <Box>
              <Typography color="text.secondary" sx={{ mb: 2.5, textAlign: 'center' }}>
                Select an event above to manage its details, registrations, timeline, and more.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, opacity: 0.45, pointerEvents: 'none' }}>
                {EVENT_SECTIONS.map(({ id, label, icon: Icon, description }) => (
                  <Card key={id} variant="outlined">
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', minHeight: 130, gap: 1, py: 2 }}>
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover', color: 'text.disabled', display: 'flex' }}>
                        <Icon sx={{ fontSize: 32 }} />
                      </Box>
                      <Box>
                        <Typography fontWeight={600} sx={{ mb: 0.5 }}>{label}</Typography>
                        <Typography variant="body2" color="text.secondary">{description}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              {EVENT_SECTIONS.map(({ id, label, icon: Icon, description }) => (
                <Card key={id} variant="outlined" sx={{ cursor: 'pointer', transition: 'box-shadow 0.15s', '&:hover': { boxShadow: 3 } }}
                  onClick={() => setActiveSection(id)}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', minHeight: 130, gap: 1, py: 2 }}>
                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.50', color: 'primary.main', display: 'flex' }}>
                      <Icon sx={{ fontSize: 32 }} />
                    </Box>
                    <Box>
                      <Typography fontWeight={600} sx={{ mb: 0.5 }}>{label}</Typography>
                      <Typography variant="body2" color="text.secondary">{description}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </>
      )}

      {/* New / edit event dialog — rendered regardless of which section is active, so Edit Details works from within Event Details too */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{dialogMode === 'edit' ? 'Edit Event' : 'New Event'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField
              label="Event Name *" size="small" fullWidth
              value={form.eventName}
              onChange={e => setForm(prev => ({ ...prev, eventName: e.target.value }))}
            />
            <TextField
              label="Location" size="small" fullWidth
              value={form.location}
              onChange={e => setForm(prev => ({ ...prev, location: e.target.value }))}
            />
            <TextField
              label="Address" size="small" fullWidth
              value={form.address}
              onChange={e => setForm(prev => ({ ...prev, address: e.target.value }))}
            />
            <TextField
              label="Start Date *" type="date" size="small" fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.startDate}
              onChange={e => setForm(prev => ({ ...prev, startDate: e.target.value }))}
            />
            <TextField
              label="End Date *" type="date" size="small" fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.endDate}
              onChange={e => setForm(prev => ({ ...prev, endDate: e.target.value }))}
            />
            <TextField
              label="Extra Information" size="small" fullWidth multiline minRows={2}
              value={form.extraInfo}
              onChange={e => setForm(prev => ({ ...prev, extraInfo: e.target.value }))}
            />
            <TextField
              label="Event Info Link" size="small" fullWidth
              helperText="Link to a page with full event details (your event info page, a Google Doc, etc.) — shown as a link on the teacher portal's Event Details page."
              value={form.infoDocUrl}
              onChange={e => setForm(prev => ({ ...prev, infoDocUrl: e.target.value }))}
              InputProps={form.infoDocUrl ? {
                endAdornment: (
                  <Link href={form.infoDocUrl} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ whiteSpace: 'nowrap', pl: 1 }}>
                    Open ↗
                  </Link>
                ),
              } : undefined}
            />

            <Divider />
            <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Capacity
            </Typography>
            <TextField
              label="Max Participants" type="number" size="small" fullWidth
              helperText="Leave blank for no cap — warning-only, see the Capacity card on Event Details"
              value={form.maxParticipants}
              onChange={e => setForm(prev => ({ ...prev, maxParticipants: e.target.value }))}
            />
            <TextField
              label="Max Entries" type="number" size="small" fullWidth
              helperText="Leave blank for no cap"
              value={form.maxEntries}
              onChange={e => setForm(prev => ({ ...prev, maxEntries: e.target.value }))}
            />
            {events.length > 1 && (
              <FormControlLabel
                control={<Checkbox checked={form.isCurrent} onChange={e => setForm(prev => ({ ...prev, isCurrent: e.target.checked }))} />}
                label="This is the currently active event (used to resolve capacity settings)"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} color="inherit" /> : dialogMode === 'edit' ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Landing page — shown on first load and whenever "Admin" (top of sidebar)
// is clicked. Just the two top-level destinations for now; meant to grow
// over time (recent activity, outstanding items, etc.) without needing to
// touch the sidebar/routing wiring above.
const HOME_TILES = [
  { id: 'events', label: 'Event Management', icon: EventNoteIcon, description: 'Event details, registrations, timeline, and more' },
  { id: 'workshops', label: 'Workshops', icon: SelfImprovementIcon, description: 'Workshop leaders, spaces, schedule and assignments' },
  { id: 'schoolAccounts', label: 'Account Management', icon: SchoolIcon, description: 'Approve school registrations, manage login credentials' },
];

// Only reachable by users with more than one area (single-area users are
// redirected off Home straight into it). areas: { [tile id]: visible }.
function AdminHome({ onNavigate, areas }) {
  const tiles = HOME_TILES.filter(t => areas[t.id]);
  return (
    <>
      <Typography variant="h6" fontWeight={600} gutterBottom>Welcome</Typography>
      <Divider sx={{ mb: 3 }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
        {tiles.map(({ id, label, icon: Icon, description }) => (
          <Card key={id} variant="outlined" sx={{ cursor: 'pointer', transition: 'box-shadow 0.15s', '&:hover': { boxShadow: 3 } }}
            onClick={() => onNavigate(id)}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', minHeight: 160, gap: 1.5, py: 3 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.50', color: 'primary.main', display: 'flex' }}>
                <Icon sx={{ fontSize: 32 }} />
              </Box>
              <Box>
                <Typography fontWeight={600} sx={{ mb: 0.5 }}>{label}</Typography>
                <Typography variant="body2" color="text.secondary">{description}</Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </>
  );
}

function PlaceholderCard({ title, description }) {
  return (
    <Card variant="outlined" sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
      <Typography fontWeight={500} gutterBottom>{title}</Typography>
      <Typography variant="body2">{description}</Typography>
    </Card>
  );
}

function EventDetailsSection({ event, onEdit, onQuickUpdate }) {
  const readOnly = useEventReadOnly();
  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
            <Typography fontWeight={600}>Event Information</Typography>
            {!readOnly && (
              <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={onEdit} disabled={!event}>
                Edit Details
              </Button>
            )}
          </Box>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Location</Typography>
              <Typography variant="body2">{event?.location || 'Not set'}</Typography>
              {event?.address && <Typography variant="body2" color="text.secondary">{event.address}</Typography>}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Event Dates</Typography>
              <Typography variant="body2">
                {event?.startDate ? `${fmtDate(event.startDate)} – ${fmtDate(event.endDate)}` : 'Not set'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Extra Information</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {event?.extraInfo || 'Not set'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Event Info Link</Typography>
              {event?.infoDocUrl ? (
                <Link href={event.infoDocUrl} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {event.infoDocUrl}
                </Link>
              ) : (
                <Typography variant="body2" color="text.secondary">Not set</Typography>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <CapacityCard event={event} onQuickUpdate={onQuickUpdate} />

      <Card variant="outlined">
        <CardContent>
          <Typography fontWeight={600} gutterBottom>Timeline</Typography>
          <EventTimelineSection event={event} />
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography fontWeight={600} gutterBottom>Prices</Typography>
          <EventPricesSection />
        </CardContent>
      </Card>
    </Stack>
  );
}

// current/max is a warning-only threshold (max participants/entries are
// never an automatic block — see the plan this was built from) — the bar
// just turns amber near the cap and red past it so the admin notices in
// time to flip one of the manual overrides below if they want to actually
// act on it.
function CapacityMeter({ label, current, max }) {
  if (!max) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
        <Typography variant="body2">{current} (no cap set)</Typography>
      </Box>
    );
  }
  const pct = Math.min(100, (current / max) * 100);
  const over = current > max;
  const warn = over || pct >= 90;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" color={over ? 'error.main' : warn ? 'warning.main' : 'text.secondary'} fontWeight={warn ? 700 : 400}>
          {current} / {max}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={over ? 'error' : warn ? 'warning' : 'primary'}
        sx={{ height: 6, borderRadius: 1, mt: 0.5 }}
      />
      {warn && (
        <Typography variant="caption" color={over ? 'error.main' : 'warning.main'} display="block" sx={{ mt: 0.5 }}>
          {over ? `${current - max} over capacity` : 'Approaching capacity'}
        </Typography>
      )}
    </Box>
  );
}

// Registration/Participants/Entries overrides are event-wide (apply to every
// school at once) — see getEffectiveLocks on the backend. A school-specific
// override for participants/entries also exists (School Registrations →
// Edit dialog) and takes precedence over whichever of these is set — it's
// the more specific setting, so an admin can always carve out an exception
// for one school regardless of the event-wide toggle here. Each
// row pairs a capacity meter with the override that's most relevant to it
// (estimated attendance ↔ new school registrations, etc.) so the toggle sits
// right beside the number it affects, instead of a separate stacked section.
function CapacityCard({ event, onQuickUpdate }) {
  const readOnly = useEventReadOnly();
  const [updatingField, setUpdatingField] = useState(null);

  const handleOverrideChange = async (field, value) => {
    if (value === null) return; // re-clicking the already-selected button in an exclusive group fires null
    setUpdatingField(field);
    try {
      await onQuickUpdate({ [field]: value });
    } finally {
      setUpdatingField(null);
    }
  };

  const overrideToggle = (field) => (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={event?.[field] || ''}
      onChange={(e, value) => handleOverrideChange(field, value)}
      // A viewer still sees which override is set, just can't change it.
      disabled={readOnly || !event || updatingField === field}
    >
      {OVERRIDE_OPTIONS.map(o => (
        <ToggleButton key={o.value} value={o.value} sx={{ px: 1.25, py: 0.25, fontSize: 12, lineHeight: 1.4 }}>
          {o.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  const row = (label, current, max, field) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, '&:last-child': { mb: 0 } }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <CapacityMeter label={label} current={current} max={max} />
      </Box>
      {overrideToggle(field)}
    </Box>
  );

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography fontWeight={600} gutterBottom>Capacity &amp; Registration</Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          "Auto" defers to the estimate-vs-max calculation (new school registrations) or the Timeline deadline (participants/entries) — "Locked"/"Open" win, in either direction, even overriding a passed deadline, unless a specific school has its own manual override set (School Registrations → Edit), which takes precedence over this.
        </Typography>
        {row('Estimated attendance (accepted schools)', event?.estimatedAttendance || 0, event?.maxParticipants || 0, 'registrationOverride')}
        {row('Participants registered', event?.participantCount || 0, event?.maxParticipants || 0, 'participantsLockOverride')}
        {row('Entries registered', event?.entryCount || 0, event?.maxEntries || 0, 'entriesLockOverride')}
      </CardContent>
    </Card>
  );
}


function EventProgramSection({ event }) {
  return (
    <Stack spacing={2}>
      <PlaceholderCard title="Program Order" description="Performance schedule, running order, intermissions" />
      <PlaceholderCard title="Program Content" description="Bios, school information, advertisements" />
    </Stack>
  );
}

function SchoolDetails({ school, onReset, resetting }) {
  const principalFields = [
    { label: 'Principal', value: school.principalName },
    { label: 'Principal Contact', value: school.principalContact },
  ].filter(f => f.value);

  const socialFields = [
    { label: 'Website', value: school.website },
    { label: 'Instagram', value: school.instagram },
    { label: 'TikTok', value: school.tiktok },
  ].filter(f => f.value);

  const reg = school.registration;
  const regFields = reg ? [
    { label: 'Est. Attendance', value: reg.estAttendance },
    { label: 'Est. Entries', value: reg.estEntries },
    { label: 'Payment Method', value: reg.paymentMethod },
    { label: 'Status', value: reg.status },
    { label: 'Fee Paid', value: reg.feePaid || 'No' },
  ].filter(f => f.value) : [];

  return (
    <Box sx={{ mt: 1.5 }}>
      {(school.fullAddress || school.schoolAddress) && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.disabled" fontWeight={600} display="block" sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Address
          </Typography>
          {/* fullAddress = street, city, province postal (built server-side) */}
          <Typography variant="body2">{school.fullAddress || school.schoolAddress}</Typography>
        </Box>
      )}
      {principalFields.length > 0 && (
        <>
          <Typography variant="caption" color="text.disabled" fontWeight={600} display="block" sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Principal
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 1.5 }}>
            {principalFields.map(({ label, value }) => (
              <Box key={label}>
                <Typography variant="caption" color="text.disabled" display="block">{label}</Typography>
                <Typography variant="body2">{value}</Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
      {socialFields.length > 0 && (
        <>
          <Divider sx={{ mb: 1 }} />
          <Typography variant="caption" color="text.disabled" fontWeight={600} display="block" sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Social Media
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 1.5 }}>
            {socialFields.map(({ label, value }) => (
              <Box key={label}>
                <Typography variant="caption" color="text.disabled" display="block">{label}</Typography>
                <Typography variant="body2">{value}</Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
      {regFields.length > 0 && (
        <>
          <Divider sx={{ mb: 1 }} />
          <Typography variant="caption" color="text.disabled" fontWeight={600} display="block" sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            2026 Event Registration
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 1.5 }}>
            {regFields.map(({ label, value }) => (
              <Box key={label}>
                <Typography variant="caption" color="text.disabled" display="block">{label}</Typography>
                <Typography variant="body2">{value}</Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
      {(school.schoolId || onReset) && (
        <>
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.disabled" display="block">School ID</Typography>
              <Typography variant="body2" fontFamily="monospace">{school.schoolId}</Typography>
            </Box>
            {onReset && (
              <Button variant="outlined" size="small" color="warning" disabled={resetting} onClick={onReset} sx={{ whiteSpace: 'nowrap' }}>
                {resetting ? <CircularProgress size={18} color="inherit" /> : 'Reset Password'}
              </Button>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
